import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";

import { EMPTY_FILTERS } from "@/components/ListingFilters";
import { cn, titleCase } from "@/lib/utils";

/**
 * The buyer dashboard's welcome hero, with an optional marketplace search.
 * Base classes are the phone arrangement; `lg:` classes widen it for web.
 */

// The marketplace (pages/Home.tsx) seeds its applied filters from this
// sessionStorage key, so writing a search here opens it pre-filtered.
const LISTINGS_FILTERS_KEY = "buyerListingsFilters";
const LISTINGS_PAGE_KEY = "buyerListingsPage";

export function WelcomeHero({
  firstName,
  planName,
  kicker,
  path,
  className,
  children,
  showSearch = true,
}: {
  firstName: string | null;
  planName: string | null;
  kicker?: React.ReactNode;
  path: (p: string) => string;
  className?: string;
  /** Rendered under the search, inside the text column (web: live position). */
  children?: React.ReactNode;
  /** The web dashboard drops the search; the phone hero keeps it. */
  showSearch?: boolean;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const search = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    try {
      sessionStorage.setItem(LISTINGS_FILTERS_KEY, JSON.stringify({ ...EMPTY_FILTERS, search: q }));
      sessionStorage.removeItem(LISTINGS_PAGE_KEY);
    } catch { /* storage blocked — still open the marketplace */ }
    navigate(path("/buyer/home"));
  };

  return (
    // public-hero-shell keeps the Dark · Gold palette on either theme;
    // hero-niva is the flat ground the Niva cutout was shot against.
    <section
      className={cn(
        "public-hero-shell hero-niva relative overflow-hidden px-5 pb-5 pt-6 text-foreground lg:px-10 lg:pb-10 lg:pt-10",
        className,
      )}
    >
      {/* Web: the cutout fills the hero's right side, beside the 56%-wide
          content column. */}
      <img
        src="/images/home-banners/niva-hero.webp"
        alt=""
        aria-hidden
        className="pointer-events-none absolute bottom-0 right-0 hidden h-full w-[46%] max-w-[34rem] object-cover object-[50%_12%] [mask-image:linear-gradient(to_right,transparent,black_35%)] lg:block"
      />
      {/* Phones: the KPI grid spans the full width, so a full-height cutout
          sat behind its right column and blurred through the glass tiles.
          Here she's confined to the greeting block's top-right corner. */}
      <div className="relative">
      <img
        src="/images/home-banners/niva-hero.webp"
        alt=""
        aria-hidden
        className="pointer-events-none absolute -right-5 -top-6 bottom-0 w-[46%] max-w-[13rem] object-cover object-[50%_10%] [mask-image:radial-gradient(ellipse_80%_75%_at_62%_38%,black_55%,transparent_100%)] lg:hidden"
      />
      <div className="relative z-10 max-w-[62%] pb-4 lg:max-w-[56%] lg:pb-6">
        {(kicker || planName) && (
          <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 lg:mb-3">
            {kicker && (
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary lg:text-[11px] lg:tracking-[0.18em]">{kicker}</span>
            )}
            {planName && (
              <span className="inline-block rounded border border-primary px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-primary lg:text-[11px]">
                {planName}
              </span>
            )}
          </div>
        )}
        {/* titleCase: names are stored exactly as typed at signup, and plenty
            arrive all-lowercase. */}
        <h1 className="heading-bold m-0 font-display text-[2rem] leading-[1.05] lg:text-[3.25rem]">
          Welcome back{firstName ? "," : "!"}
          {firstName && (
            <>
              <br />
              <span className="text-primary">{titleCase(firstName)}!</span>
            </>
          )}
        </h1>
        <p className="mb-0 mt-2 text-sm leading-snug text-foreground/85 lg:mt-3 lg:text-lg">
          Let's find your next off-market business.
        </p>
      </div>
      </div>
      {/* A literal light pill: the hero is always dark, and bg-white would
          be remapped to the dark surface by the theme. */}
      {showSearch && (
      <form onSubmit={search} role="search" className="relative z-10 lg:max-w-xl">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8c8171]" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search businesses, industries, locations…"
          aria-label="Search listings"
          className="w-full rounded-xl border-0 bg-[#fbf7ef] py-3 pl-10 pr-3 text-base text-[#1a1408] shadow-lg outline-none placeholder:text-[#8c8171] focus-visible:ring-2 focus-visible:ring-primary lg:py-3.5"
        />
      </form>
      )}
      {children && <div className={cn("relative z-10 lg:max-w-[56%]", showSearch && "mt-6")}>{children}</div>}
    </section>
  );
}
